import PublicHeader from '@/components/PublicHeader'
import PublicFooter from '@/components/PublicFooter'

export default function MakePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      <PublicHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold mb-4 gradient-text">GistMag for Make.com</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
            Automatically generate AI-powered alt text for your images
          </p>
          <a
            href="https://www.make.com/en/hq/app-invitation/13425288b9cf9bb84e01060543c1b621"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 dark:bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition hover-lift text-lg"
          >
            <span>Install GistMag in Make.com</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
            GistMag uses advanced AI to create descriptive, accessible, and SEO-friendly alt text for images in your Make.com workflows.
          </p>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">🚀</span>
              Quick Start
            </h2>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>✓ Available for All Plans:</strong> The Make.com integration is available for all GistMag subscription plans, including the Free plan. You can start using it immediately with any plan!
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">1. Get Your API Key</h3>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Sign up at <a href="https://www.gistmag.co.uk" className="text-primary-600 dark:text-primary-400 hover:underline">gistmag.co.uk</a></li>
                  <li>Go to your Dashboard → Settings → API Keys</li>
                  <li>Copy your API key</li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">2. Install GistMag in Make.com</h3>
                <div className="mb-4">
                  <a
                    href="https://www.make.com/en/hq/app-invitation/13425288b9cf9bb84e01060543c1b621"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 dark:bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition hover-lift"
                  >
                    <span>Install GistMag App</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Or manually add the app:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Add a GistMag module to your scenario</li>
                  <li>Click <strong>Create a connection</strong></li>
                  <li>Paste your API key</li>
                  <li>Click <strong>Save</strong></li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">3. Start Generating Alt Text</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Fill in the image URL and let AI do the rest!
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">📦</span>
              Available Modules
            </h2>

            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold mb-4">Generate Alt Text</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">Generate alt text for a single image.</p>
                
                <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">What you need:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li>Image URL (required)</li>
                    <li>Mode: Short, Long, or SEO (optional, default: Short)</li>
                    <li>Language (optional, default: English)</li>
                  </ul>
                </div>

                <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">What you get:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li><code className="bg-gray-200 dark:bg-slate-700 px-2 py-1 rounded">alt_text</code> - Generated alt text based on your selected mode</li>
                    <li><code className="bg-gray-200 dark:bg-slate-700 px-2 py-1 rounded">short_alt_text</code> - Concise version (50-100 characters)</li>
                    <li><code className="bg-gray-200 dark:bg-slate-700 px-2 py-1 rounded">long_alt_text</code> - Detailed description (100-200 characters)</li>
                    <li><code className="bg-gray-200 dark:bg-slate-700 px-2 py-1 rounded">seo_alt_text</code> - SEO-optimized version</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                  Example use: Auto-generate alt text when new images are uploaded to Google Drive or WordPress.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">Batch Process Images</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">Generate alt text for multiple images at once.</p>
                
                <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">What you need:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li>Array of images with URLs</li>
                    <li>Each image can have its own mode and language</li>
                  </ul>
                </div>

                <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">What you get:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li>One bundle per image with all alt text variants</li>
                    <li>Total count, successful, and failed statistics</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 italic mb-2">
                  Example use: Process an entire product catalog from a spreadsheet.
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  <strong>Note:</strong> This module outputs one bundle per image, making it easy to process results individually.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">Get Usage Statistics</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">Check your API usage and remaining credits.</p>
                
                <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">What you need:</h4>
                  <p className="text-gray-700 dark:text-gray-300">Nothing! Just your connection</p>
                </div>

                <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">What you get:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li>Images used this month</li>
                    <li>Monthly limit</li>
                    <li>Remaining images</li>
                    <li>Subscription tier (free/pro/enterprise)</li>
                    <li>Usage breakdown by source (web, API, WordPress, etc.)</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                  Example use: Set up a daily check to alert you when credits are running low.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">Verify API Key</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">Check if your API key is valid.</p>
                
                <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">What you need:</h4>
                  <p className="text-gray-700 dark:text-gray-300">Nothing! Just your connection</p>
                </div>

                <div className="bg-gray-100 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">What you get:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li>Valid status (true/false)</li>
                    <li>Subscription tier</li>
                    <li>Message</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                  Example use: Test your connection or validate setup.
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">💡</span>
              Common Use Cases
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">Auto Alt Text for New Images</h3>
                <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 text-sm font-mono text-green-400">
                  <div>Google Drive: Watch Files (in a folder)</div>
                  <div className="ml-4">↓</div>
                  <div className="ml-4">GistMag: Generate Alt Text</div>
                  <div className="ml-8">↓</div>
                  <div className="ml-8">Google Sheets: Add Row (save URL and alt text)</div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Batch Process Product Images</h3>
                <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 text-sm font-mono text-green-400">
                  <div>Google Sheets: Get Rows (product images)</div>
                  <div className="ml-4">↓</div>
                  <div className="ml-4">GistMag: Batch Process Images</div>
                  <div className="ml-8">↓</div>
                  <div className="ml-8">Google Sheets: Update Rows (add alt text)</div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Monitor Usage</h3>
                <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 text-sm font-mono text-green-400">
                  <div>Schedule: Every day at 9 AM</div>
                  <div className="ml-4">↓</div>
                  <div className="ml-4">GistMag: Get Usage Statistics</div>
                  <div className="ml-8">↓</div>
                  <div className="ml-8">Filter: If remaining &lt; 100</div>
                  <div className="ml-12">↓</div>
                  <div className="ml-12">Email: Send low credit alert</div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">WordPress Blog Images</h3>
                <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 text-sm font-mono text-green-400">
                  <div>WordPress: Watch New Media</div>
                  <div className="ml-4">↓</div>
                  <div className="ml-4">GistMag: Generate Alt Text (SEO mode)</div>
                  <div className="ml-8">↓</div>
                  <div className="ml-8">WordPress: Update Media (set alt text)</div>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">🎯</span>
              Mode Guide
            </h2>

            <p className="text-gray-700 dark:text-gray-300 mb-6">Choose the right mode for your needs:</p>

            <div className="space-y-4">
              <div className="border-l-4 border-primary-500 pl-4">
                <h3 className="text-xl font-semibold mb-2">Short - Quick, concise descriptions</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 mb-2">
                  <li>Best for: Icons, thumbnails, navigation images</li>
                  <li>Length: 50-100 characters</li>
                </ul>
                <p className="text-sm italic text-gray-600 dark:text-gray-400">
                  Example: "Golden retriever in park"
                </p>
              </div>

              <div className="border-l-4 border-primary-500 pl-4">
                <h3 className="text-xl font-semibold mb-2">Long - Detailed descriptions</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 mb-2">
                  <li>Best for: Hero images, complex scenes, infographics</li>
                  <li>Length: 100-200 characters</li>
                </ul>
                <p className="text-sm italic text-gray-600 dark:text-gray-400">
                  Example: "A happy golden retriever running through a lush green park on a sunny day, chasing after a red ball"
                </p>
              </div>

              <div className="border-l-4 border-primary-500 pl-4">
                <h3 className="text-xl font-semibold mb-2">SEO - Optimized for search engines</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 mb-2">
                  <li>Best for: Product images, blog posts, landing pages</li>
                  <li>Includes relevant keywords</li>
                </ul>
                <p className="text-sm italic text-gray-600 dark:text-gray-400">
                  Example: "Golden retriever dog playing fetch in park - pet photography"
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">🌍</span>
              Supported Languages
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              English • Spanish • French • German • Italian • Portuguese • Dutch • Polish • Russian • Japanese • Chinese (Simplified)
            </p>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">📊</span>
              Pricing & Limits
            </h2>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-800 dark:text-green-300">
                <strong>✓ Make.com Available for All Plans:</strong> The Make.com integration works with Free, Starter, Pro, Business, and Enterprise plans. Only your credit limits differ by plan.
              </p>
            </div>
            <div className="space-y-3 text-gray-700 dark:text-gray-300">
              <p><strong>Free Plan:</strong> 50 images (one-time)</p>
              <p><strong>Starter Plan:</strong> 500 images/month</p>
              <p><strong>Pro Plan:</strong> 1,000 images/month</p>
              <p><strong>Business Plan:</strong> 5,000 images/month</p>
              <p><strong>Enterprise:</strong> 100,000 images/month</p>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              Check your usage anytime with the <strong>Get Usage Statistics</strong> module.
            </p>
            <p className="mt-2">
              <a href="/dashboard/billing" className="text-primary-600 dark:text-primary-400 hover:underline">
                Upgrade your plan at gistmag.co.uk →
              </a>
            </p>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">⚠️</span>
              Common Issues
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">"Invalid or missing API key"</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                  <li>Double-check your API key in the GistMag dashboard</li>
                  <li>Make sure you copied it correctly (no extra spaces)</li>
                  <li>Try regenerating your key</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">"Rate limit exceeded"</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                  <li>You've reached your monthly limit</li>
                  <li>Check usage with the Get Usage Statistics module</li>
                  <li>Upgrade your plan at <a href="/dashboard/billing" className="text-primary-600 dark:text-primary-400 hover:underline">gistmag.co.uk</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">"Value is not a valid URL address"</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                  <li>Make sure your image URL is publicly accessible</li>
                  <li>URL must start with http:// or https://</li>
                  <li>Supported formats: JPEG, PNG, WebP, GIF</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Batch module returns one bundle instead of multiple</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  This is normal! The module outputs one bundle per image. Each bundle contains the alt text for that image. Perfect for processing results one at a time.
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">💬</span>
              Tips
            </h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li><strong>Test first</strong> - Start with the "Generate Alt Text" module on a single image before batch processing</li>
              <li><strong>Monitor usage</strong> - Set up a daily scenario to check your remaining credits</li>
              <li><strong>Choose the right mode</strong> - Use SEO mode for public-facing images, Short for icons</li>
              <li><strong>Use filters</strong> - Add filters after batch processing to handle successful vs failed results</li>
              <li><strong>Cache results</strong> - Store generated alt text to avoid regenerating for the same image</li>
            </ol>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">🔗</span>
              Helpful Links
            </h2>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>
                <a 
                  href="https://www.make.com/en/hq/app-invitation/13425288b9cf9bb84e01060543c1b621" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary-600 dark:text-primary-400 hover:underline font-semibold"
                >
                  Install GistMag in Make.com →
                </a>
              </li>
              <li>
                <a href="https://www.gistmag.co.uk" className="text-primary-600 dark:text-primary-400 hover:underline">
                  GistMag Website
                </a>
              </li>
              <li>
                <a href="/docs" className="text-primary-600 dark:text-primary-400 hover:underline">
                  API Documentation
                </a>
              </li>
              <li>
                <a href="https://academy.make.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                  Make.com Academy
                </a>
              </li>
              <li>
                <a href="/dashboard" className="text-primary-600 dark:text-primary-400 hover:underline">
                  Get Support
                </a>
              </li>
            </ul>
          </div>

          <div className="glass-card rounded-xl p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <span className="mr-3">📝</span>
              Need Help?
            </h2>
            <div className="space-y-2 text-gray-700 dark:text-gray-300">
              <p>
                <strong>For GistMag API issues:</strong> Contact GistMag support at <a href="https://www.gistmag.co.uk" className="text-primary-600 dark:text-primary-400 hover:underline">gistmag.co.uk</a>
              </p>
              <p>
                <strong>For Make.com integration help:</strong> Check the Make.com community forums
              </p>
            </div>
          </div>

          <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8">
            <p><strong>Version 1.0.0</strong> • Built for Make.com • Powered by GistMag AI</p>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  )
}

